import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import GroupOutlined from '@mui/icons-material/GroupOutlined'

import { loadMemberReport } from '../../actions/users'
import { showNotification } from '../../actions/ui'
import { useTypedSelector } from '../../reducers'
import {
  buildFolderTree,
  buildUserTree,
  MemberReportEntry,
  onlyUsers,
  parseMemberReport
} from '../../helpers/memberReport'
import { saveBlob } from '../../helpers/download'
import { formatDate } from '../../utils/dateFormat'
import SearchInput from '../SearchInput'
import RefreshButton from '../buttons/RefreshButton'
import DownloadButton from '../buttons/DownloadButton'
import { FolderTreeGrid, UserTreeGrid } from './ReportGrids'
import FolderData from '../icons/FolderData'
import { ActiveLabel, StyledLinkTab, StyledTabsRoot } from '../AppNav'

type View = 'folders' | 'users'

// The shared theme gives every disabled button a 48px height, which overrides
// the small size (40px) and makes these grow while a report loads.
const keepSmallWhenDisabled = { '&.Mui-disabled': { height: '2.5rem' } }

const TABS = [
  { value: 'folders', messageId: 'memberReport.byFolder', Icon: FolderData },
  { value: 'users', messageId: 'memberReport.byUser', Icon: GroupOutlined }
] as const

const matches = (entry: MemberReportEntry, query: string) =>
  [entry.name, entry.email, entry.path.join('/')].some(value =>
    value.toLowerCase().includes(query)
  )

const MemberReportDialog = ({
  open,
  onClose,
  tenantId
}: {
  open: boolean
  onClose: () => void
  tenantId: string
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const { status, csv, generatedAt } = useTypedSelector(
    state => state['users'].memberReport
  )
  const [view, setView] = useState<View>('folders')
  const [searchText, setSearchText] = useState('')

  // Opening reuses this tab's cached report; Refresh generates a new one.
  useEffect(() => {
    if (open) {
      setSearchText('')
      dispatch(loadMemberReport(tenantId))
    }
  }, [open, tenantId, dispatch])

  // The download keeps every principal; the dialog lists users only.
  const entries = useMemo(() => onlyUsers(parseMemberReport(csv)), [csv])
  const query = searchText.trim().toLowerCase()
  const visibleEntries = useMemo(
    () => (query ? entries.filter(entry => matches(entry, query)) : entries),
    [entries, query]
  )

  const rootLabel = intl.formatMessage({ id: 'memberReport.siteRoot' })
  const folderRows = useMemo(() => buildFolderTree(visibleEntries, rootLabel), [
    visibleEntries,
    rootLabel
  ])
  const userRows = useMemo(() => buildUserTree(visibleEntries, rootLabel), [
    visibleEntries,
    rootLabel
  ])

  const onDownload = () => {
    // e.g. user-access-report-06-Oct-2026.csv
    const date = generatedAt ? formatDate(generatedAt).replace(/ /g, '-') : ''
    const fileName = `user-access-report-${date}.csv`
    saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName)
    dispatch(showNotification(`Downloaded ${fileName}`))
  }

  const loading = status === 'loading'
  const loaded = status === 'loaded'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={fullScreen}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box>
          {intl.formatMessage({ id: 'memberReport.title' })}
          {loaded && generatedAt && (
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage(
                { id: 'memberReport.generatedAt' },
                {
                  time: intl.formatTime(generatedAt),
                  date: formatDate(generatedAt)
                }
              )}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RefreshButton
            variant="outlined"
            size="small"
            sx={keepSmallWhenDisabled}
            disabled={loading}
            onClick={() => dispatch(loadMemberReport(tenantId, true))}
          />
          <Tooltip
            title={intl.formatMessage({ id: 'memberReport.downloadHint' })}
          >
            {/* A disabled button fires no events, so the span holds the tooltip. */}
            <span>
              <DownloadButton
                variant="outlined"
                size="small"
                sx={keepSmallWhenDisabled}
                disabled={!loaded}
                onClick={onDownload}
              />
            </span>
          </Tooltip>
          <IconButton aria-label="Close" size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: fullScreen ? undefined : '75vh'
        }}
      >
        {loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <CircularProgress />
            <Typography
              color="textSecondary"
              sx={{ textAlign: 'center', maxWidth: 480 }}
            >
              {intl.formatMessage({ id: 'memberReport.loading' })}
            </Typography>
          </Box>
        )}

        {status === 'error' && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <Typography>
              {intl.formatMessage({ id: 'memberReport.error' })}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => dispatch(loadMemberReport(tenantId))}
            >
              {intl.formatMessage({ id: 'memberReport.retry' })}
            </Button>
          </Box>
        )}

        {loaded && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 2,
                mb: 1
              }}
            >
              <StyledTabsRoot
                value={view}
                onChange={(_event, value) => setView(value)}
                indicatorColor="secondary"
                textColor="primary"
                sx={{ pl: 0, borderBottom: 0 }}
              >
                {TABS.map(({ value, messageId, Icon }) => {
                  const label = intl.formatMessage({ id: messageId })
                  const active = view === value
                  return (
                    <StyledLinkTab
                      key={value}
                      value={value}
                      iconPosition="start"
                      icon={<Icon color={active ? 'secondary' : undefined} />}
                      label={
                        active ? (
                          <ActiveLabel>{label}</ActiveLabel>
                        ) : (
                          <span>{label}</span>
                        )
                      }
                    />
                  )
                })}
              </StyledTabsRoot>
              <SearchInput
                id="member-report-search-input"
                placeholder={intl.formatMessage({
                  id: 'memberReport.searchPlaceholder'
                })}
                text={searchText}
                onTextChange={setSearchText}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 300 }}>
              {/* Remount on search so the grid re-applies its expansion. */}
              {view === 'folders' ? (
                <FolderTreeGrid
                  key={query ? 'search' : 'all'}
                  rows={folderRows}
                  expandAll={Boolean(query)}
                />
              ) : (
                <UserTreeGrid
                  key={query ? 'search' : 'all'}
                  rows={userRows}
                  expandAll={Boolean(query)}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default MemberReportDialog
